/**
 * SiteDetailModal — Site detail popup opened from a Job Board Site link (Quick 260722).
 *
 * A job's pickup site is either a hauler_location (tank battery / compressor
 * station / rig — DEC-147 site_type) or a standalone well (DEC-166: pickup_well_id
 * set, no hauler_locations row — the 86bazf72j case). The row exposes `siteId`
 * (= hauler_location_id) for the former and `pickupWellId` for the latter, so the
 * modal routes to the matching detail endpoint:
 *   - hauler_location → GET /v1/tank-batteries/:id (editable name)
 *   - standalone well → GET /v1/wells/:id (READ-ONLY: wells are the OCC platform
 *     catalog — there is no hauler PATCH endpoint for a well)
 *
 * Edit (hauler_location only) is gated by MANAGE_HAULER_CATALOG (DISPATCH_MANAGER+)
 * — a local ReadonlySet UX gate mirroring JobDetailModal's cancel gate; the server
 * (tank-batteries.ts PATCH) remains authoritative and applies the DEC-147
 * upperCaseIdentifier normalization (never relaxed/duplicated client-side). Save
 * invalidates the jobs list so the Site column reflects the rename.
 *
 * Style: JobDetailModal's Dialog / Field patterns.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { useTankBattery, useWellDetail, useRenameTankBattery } from '../sites-operators/hooks';
import { useUserProfile } from '../../lib/use-user-timezone';
// MANAGE_HAULER_CATALOG is DISPATCH_MANAGER+ (server-authoritative — this set is a
// UX gate only). DEC-313 union applied in lib/role-gates.ts.
import { MANAGE_CATALOG_ROLES } from '../../lib/role-gates';

/** The site a Job Board row points at (from the /v1/jobs list row). */
export interface SiteRef {
  /** hauler_location_id — set for a tank battery / compressor station / rig. */
  readonly siteId: string | null;
  /** wells.id — set for a standalone-well pickup (no hauler_location). */
  readonly pickupWellId: string | null;
  readonly siteName: string;
}

export interface SiteDetailModalProps {
  readonly site: SiteRef | null;
  readonly onClose: () => void;
}

type SiteKind = 'LOCATION' | 'WELL' | 'UNKNOWN';

/** Which detail endpoint a site resolves to. */
export function siteKind(site: SiteRef | null): SiteKind {
  if (site === null) return 'UNKNOWN';
  if (site.siteId != null && site.siteId !== '') return 'LOCATION';
  if (site.pickupWellId != null && site.pickupWellId !== '') return 'WELL';
  return 'UNKNOWN';
}

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--color-text-tertiary)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '40px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};

function Field({ label, value }: { label: string; value: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
      <span style={labelStyle}>{label}</span>
      <span
        style={{
          fontSize: '14px',
          lineHeight: 1.4,
          color: 'var(--color-text-primary)',
          wordBreak: 'break-word',
        }}
      >
        {value ?? '—'}
      </span>
    </div>
  );
}

/** Format a nullable lat/lng pair for display, or null when neither is present. */
function formatCoords(lat: string | null, lng: string | null): string | null {
  if ((lat == null || lat === '') && (lng == null || lng === '')) return null;
  return `${lat ?? '—'}, ${lng ?? '—'}`;
}

export function SiteDetailModal({ site, onClose }: SiteDetailModalProps): React.JSX.Element {
  const open = site !== null;
  const kind = siteKind(site);
  // The detail hooks disable themselves on an empty id (enabled: id.length > 0),
  // so only the matching one actually fetches.
  const locationId = kind === 'LOCATION' ? (site?.siteId ?? '') : '';
  const wellId = kind === 'WELL' ? (site?.pickupWellId ?? '') : '';

  const battery = useTankBattery(locationId);
  const wellQuery = useWellDetail(wellId);

  const { data: profileData } = useUserProfile();
  const role = profileData?.profile.role ?? '';
  const canManage = MANAGE_CATALOG_ROLES.has(role);

  const queryClient = useQueryClient();
  const rename = useRenameTankBattery(locationId);

  const [editing, setEditing] = React.useState(false);
  const [name, setName] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  // Reset edit state whenever a different site opens.
  React.useEffect(() => {
    setEditing(false);
    setError(null);
  }, [site?.siteId, site?.pickupWellId]);

  const bat = battery.data?.tankBattery;
  const well = wellQuery.data?.well;

  const beginEdit = React.useCallback((): void => {
    setName(bat?.name ?? '');
    setError(null);
    setEditing(true);
  }, [bat?.name]);

  const handleSave = React.useCallback((): void => {
    const trimmed = name.trim();
    if (trimmed === '') {
      setError('Name is required.');
      return;
    }
    setError(null);
    // Server applies the DEC-147 upperCaseIdentifier normalization; we send the
    // raw value and do NOT normalize/relax it client-side.
    rename
      .mutateAsync({ name: trimmed })
      .then(() => {
        // Reflect the rename on the Job Board (Site column) + anywhere jobs render.
        void queryClient.invalidateQueries({ queryKey: ['jobs'] });
        toast.success('Site updated');
        setEditing(false);
      })
      .catch(() => {
        setError('Could not save. Check your input and try again.');
      });
  }, [name, rename, queryClient]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next): void => {
        if (!next) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{site?.siteName ?? 'Site'}</DialogTitle>
        </DialogHeader>

        {kind === 'LOCATION' ? (
          battery.isLoading ? (
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>Loading…</p>
          ) : battery.isError || bat === undefined ? (
            <p role="alert" style={{ color: 'var(--color-danger)', fontSize: '14px' }}>
              Could not load this site.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {editing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label htmlFor="site-name" style={labelStyle}>
                    Name
                  </label>
                  <input
                    id="site-name"
                    value={name}
                    onChange={(e): void => setName(e.target.value)}
                    style={inputStyle}
                    aria-label="Site name"
                  />
                  <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
                    Saved in UPPERCASE (identifier normalization).
                  </span>
                </div>
              ) : (
                <Field label="Name" value={bat.name} />
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <Field label="Site type" value={bat.siteTypeLabel ?? '—'} />
                <Field
                  label="Customer"
                  value={`${bat.operatorName} (${bat.operatorAbbreviation})`}
                />
                <Field label="Pickup" value={bat.canPickup ? 'Yes' : 'No'} />
                <Field label="Dropoff" value={bat.canDropoff ? 'Yes' : 'No'} />
                <Field label="Wells" value={String(bat.wellCount)} />
                <Field label="Tanks" value={String(bat.tankCount)} />
              </div>
              {error !== null && (
                <p
                  role="alert"
                  style={{ color: 'var(--color-danger)', fontSize: '13px', margin: 0 }}
                >
                  {error}
                </p>
              )}
            </div>
          )
        ) : kind === 'WELL' ? (
          wellQuery.isLoading ? (
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>Loading…</p>
          ) : wellQuery.isError || well === undefined ? (
            <p role="alert" style={{ color: 'var(--color-danger)', fontSize: '14px' }}>
              Could not load this site.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <Field label="Name" value={well.wellName} />
                <Field label="Site type" value="Well" />
                <Field label="API number" value={well.apiNumber} />
                <Field label="Operator" value={well.operatorName ?? '—'} />
                <Field
                  label="Location"
                  value={
                    formatCoords(well.wellheadLat, well.wellheadLng) ??
                    [well.county, well.state].filter(Boolean).join(', ') ??
                    '—'
                  }
                />
                <Field label="Well type" value={well.wellType ?? '—'} />
              </div>
              <p style={{ fontSize: '12px', color: 'var(--color-text-tertiary)', margin: 0 }}>
                This well is managed in the platform (OCC) catalog and isn&rsquo;t editable here.
              </p>
            </div>
          )
        ) : (
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>
            No further detail is available for this site.
          </p>
        )}

        <DialogFooter>
          {kind === 'LOCATION' && bat !== undefined && editing ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={(): void => {
                  setEditing(false);
                  setError(null);
                }}
                disabled={rename.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={rename.isPending}
                style={{
                  backgroundColor: 'var(--color-accent-blue)',
                  color: 'var(--color-on-accent)',
                  borderColor: 'var(--color-accent-blue)',
                }}
              >
                {rename.isPending ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              {kind === 'LOCATION' && bat !== undefined && canManage && (
                <Button type="button" variant="outline" onClick={beginEdit}>
                  Edit
                </Button>
              )}
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
